import { useMemo, useState } from 'react';
import { BatteryCharging, Calculator, Zap } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { estimateAcCharge, estimateDcCharge } from '@/lib/calc/charging';
import { calcTco } from '@/lib/calc/tco';
import { formatNumber, formatPrice } from '@/lib/formatters';

interface CarCalculatorsProps {
  rangeKm?: number | null;
  batteryKwh?: number | null;
  dcKw?: number | null;
  dc1080Min?: number | null;
  acKw?: number | null;
  consumptionWhKm?: number | null;
  annualTaxDkk?: number | null;
}

const boxOptions = [3.7, 7.4, 11, 22];

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border/60 py-2 last:border-0">
      <div className="min-w-0">
        <p className="text-[13px] leading-snug text-muted-foreground sm:text-sm">{label}</p>
        {hint && <p className="text-[11px] leading-snug text-muted-foreground/80">{hint}</p>}
      </div>
      <p className="whitespace-nowrap text-[13px] font-semibold sm:text-sm">{value}</p>
    </div>
  );
}


/**
 * Beregnere på bilsiden: ladetid og driftsøkonomi.
 * Alle forudsætninger kan justeres, så tallene er gennemsigtige.
 */
export function CarCalculators({
  rangeKm,
  batteryKwh,
  dcKw,
  dc1080Min,
  acKw,
  consumptionWhKm,
  annualTaxDkk,
}: CarCalculatorsProps) {
  const [boxKw, setBoxKw] = useState(11);

  const [kmPerYear, setKmPerYear] = useState(15000);
  const [years, setYears] = useState(5);
  const [homeShare, setHomeShare] = useState(80);
  const [homePrice, setHomePrice] = useState(2.2);
  const [publicPrice, setPublicPrice] = useState(4.5);

  const dc = useMemo(
    () =>
      batteryKwh && dcKw
        ? estimateDcCharge({
            batteryKwh,
            dcKw,
            measured1080Min: dc1080Min ?? null,
            consumptionWhKm: consumptionWhKm ?? null,
          })
        : null,
    [batteryKwh, dcKw, dc1080Min, consumptionWhKm],
  );

  const ac = useMemo(
    () => (batteryKwh ? estimateAcCharge({ batteryKwh, boxKw, carAcKw: acKw ?? null }) : null),
    [batteryKwh, boxKw, acKw],
  );

  const tco = useMemo(() => {
    const consumption =
      consumptionWhKm ?? (batteryKwh && rangeKm ? Math.round((batteryKwh * 1000) / rangeKm) : null);
    if (!consumption) return null;
    return calcTco({
      kmPerYear,
      years,
      consumptionWhKm: consumption,
      homeShare: homeShare / 100,
      homePricePerKwh: homePrice,
      publicPricePerKwh: publicPrice,
      annualTaxDkk: annualTaxDkk ?? 0,
      insuranceDkk: 6000,
      serviceDkk: 3500,
    });
  }, [consumptionWhKm, batteryKwh, rangeKm, kmPerYear, years, homeShare, homePrice, publicPrice, annualTaxDkk]);

  if (!dc && !ac && !tco) return null;

  return (
    <section aria-label="Beregnere" className="mb-6 md:mb-8">
      <h2 className="mb-2.5 font-display text-[19px] font-bold sm:text-xl">
        Beregn med dine forudsætninger
      </h2>
      <Tabs defaultValue={dc || ac ? 'charge' : 'tco'} className="w-full">
        <TabsList className="grid h-10 w-full grid-cols-2 rounded-lg bg-muted p-1">
          {(dc || ac) && (
            <TabsTrigger value="charge" className="h-8 gap-1.5 rounded-md text-[13px] sm:text-sm">
              <Zap className="h-4 w-4" aria-hidden /> Opladning
            </TabsTrigger>
          )}
          {tco && (
            <TabsTrigger value="tco" className="h-8 gap-1.5 rounded-md text-[13px] sm:text-sm">
              <Calculator className="h-4 w-4" aria-hidden /> Driftsøkonomi
            </TabsTrigger>
          )}
        </TabsList>


        {(dc || ac) && (
          <TabsContent value="charge" className="mt-2.5 rounded-xl border border-border bg-card p-4 sm:p-5">
            <div className="grid gap-5 divide-y divide-border/60 md:grid-cols-2 md:gap-6 md:divide-y-0">
              {dc && (
                <div>
                  <h3 className="mb-1.5 flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <Zap className="h-3.5 w-3.5 text-accent" aria-hidden /> Hurtigladning (DC)
                  </h3>
                  <p className="font-display text-[28px] font-bold leading-none tracking-tight sm:text-3xl">
                    {dc.minutes1080} min
                  </p>
                  <p className="mb-2.5 mt-1 text-[13px] text-muted-foreground">
                    10–80 % {dc.isEstimated ? '(vores estimat)' : '(oplyst af producenten)'}
                  </p>
                  <Row label="Peak-effekt" value={`${dcKw} kW`} />
                  <Row
                    label="Gennemsnit 10–80 %"
                    value={`ca. ${dc.avgKw} kW`}
                    hint="Vores estimat – ladekurven falder over 50 %"
                  />
                  {dc.kmPer10Min && (
                    <Row
                      label="Rækkevidde pr. 10 min"
                      value={`ca. ${dc.kmPer10Min} km`}
                      hint="Vores estimat ud fra forbrug og ladeeffekt"
                    />
                  )}
                  <p className="mt-2.5 text-[11px] leading-snug text-muted-foreground">
                    Gennemsnit og rækkevidde pr. 10 min er MinNyeElbil.dk's egne beregninger, ikke producenttal.
                  </p>
                </div>
              )}
              {ac && (
                <div className="pt-5 md:pt-0">
                  <h3 className="mb-1.5 flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <BatteryCharging className="h-3.5 w-3.5 text-accent" aria-hidden /> Hjemmeladning (AC)
                  </h3>
                  <p className="font-display text-[28px] font-bold leading-none tracking-tight sm:text-3xl">
                    {ac.hours} timer
                  </p>
                  <p className="mb-3 mt-1 text-[13px] text-muted-foreground">
                    20 → 100 % ved {ac.effectiveKw} kW
                  </p>
                  <Label className="text-[13px] text-muted-foreground">Din ladeboks</Label>
                  <div className="mb-2.5 mt-1.5 grid grid-cols-4 gap-1 rounded-lg bg-muted p-1">
                    {boxOptions.map((kw) => (
                      <button
                        key={kw}
                        type="button"
                        onClick={() => setBoxKw(kw)}
                        aria-pressed={boxKw === kw}
                        className={`h-8 rounded-md text-[13px] font-medium transition-colors ${
                          boxKw === kw
                            ? 'bg-card text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {kw} kW
                      </button>
                    ))}
                  </div>
                  {ac.limitedByCar && (
                    <p className="text-[11px] leading-snug text-muted-foreground">
                      Bilen kan maks tage {acKw} kW AC, så en kraftigere ladeboks lader ikke hurtigere.
                    </p>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        )}


        {tco && (
          <TabsContent value="tco" className="mt-2.5 rounded-xl border border-border bg-card p-4 sm:p-5">
            <div className="grid gap-5 md:grid-cols-2 md:gap-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-[13px] text-muted-foreground sm:text-sm">Kørsel pr. år: {formatNumber(kmPerYear)} km</Label>
                  <Slider
                    className="mt-2.5"
                    value={[kmPerYear]}
                    min={5000}
                    max={40000}
                    step={1000}
                    onValueChange={(v) => setKmPerYear(v[0])}
                  />
                </div>
                <div>
                  <Label className="text-[13px] text-muted-foreground sm:text-sm">Periode: {years} år</Label>
                  <Slider
                    className="mt-2.5"
                    value={[years]}
                    min={1}
                    max={10}
                    step={1}
                    onValueChange={(v) => setYears(v[0])}
                  />
                </div>
                <div>
                  <Label className="text-[13px] text-muted-foreground sm:text-sm">Hjemmeladning: {homeShare} %</Label>
                  <Slider
                    className="mt-2.5"
                    value={[homeShare]}
                    min={0}
                    max={100}
                    step={5}
                    onValueChange={(v) => setHomeShare(v[0])}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label className="text-[13px] text-muted-foreground sm:text-sm">Hjemme: {homePrice.toFixed(2)} kr/kWh</Label>
                    <Slider
                      className="mt-2.5"
                      value={[homePrice]}
                      min={1}
                      max={5}
                      step={0.1}
                      onValueChange={(v) => setHomePrice(v[0])}
                    />
                  </div>
                  <div>
                    <Label className="text-[13px] text-muted-foreground sm:text-sm">Offentlig: {publicPrice.toFixed(2)} kr/kWh</Label>
                    <Slider
                      className="mt-2.5"
                      value={[publicPrice]}
                      min={2}
                      max={8}
                      step={0.1}
                      onValueChange={(v) => setPublicPrice(v[0])}
                    />
                  </div>
                </div>
              </div>
              <div>
                <p className="font-display text-[28px] font-bold leading-none tracking-tight sm:text-3xl">{formatPrice(tco.totalPerYear)}</p>
                <p className="mb-2.5 mt-1 text-[13px] text-muted-foreground">pr. år i drift</p>
                <Row label="Strøm" value={`${formatPrice(tco.energyPerYear)} / år`} hint={`${formatNumber(tco.kwhPerYear)} kWh pr. år`} />
                <Row
                  label="Faste udgifter"
                  value={`${formatPrice(tco.fixedPerYear)} / år`}
                  hint="Ejerafgift, forsikring (6.000 kr), service og dæk (3.500 kr)"
                />
                <Row label="Pris pr. km" value={`${tco.perKm.toFixed(2)} kr`} />
                <Row label={`Samlet over ${years} år`} value={formatPrice(tco.totalPeriod)} />
                <p className="mt-2.5 text-[11px] leading-snug text-muted-foreground">
                  Uden afskrivning og finansiering. Forbrug er WLTP + 12 % for reelt forbrug og ladetab.
                </p>
              </div>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </section>
  );
}
